import { ajax } from 'rxjs/ajax'
import { throwError, from, of } from 'rxjs'
import { catchError, map, mapTo, mergeMap } from 'rxjs/operators'
import VoltError from 'VoltError'
import MockLogger from 'MockLogger'
import { KJUR } from 'jsrsasign'
import DataHelper from 'framework/helpers/data'
import CryptoJS from 'generated-libs/crypto-js'
import config from './config'
import ConfigHelper from 'framework/helpers/config'
import secureLocalStorage from 'react-secure-storage'

const JWTTokenExpiryTime = 24 //  hours
const jumpCallPeriodically = 60 * 60 // 1hr for jump API fetch

export default class RecoApi {
    /**
     *
     * @param {Object} config configuration, provided by the Consumer Project
     * @param {Object} otherApi
     */
    constructor(config) {
        this.config = config
        this.logger = (config.logger || MockLogger)
            .createChildInstance('ooredo')
            .createChildInstance('recommendations')
        this.getJWTToken().then((res) => {
            this.jwtToken = res
        })
    }

    //  check the token expiry time
    isOlderThan24Hours(
        oldTimestamp = DataHelper.getInstance().getData(DataHelper.STORE_KEY.RECOJWT_START_TIME)
    ) {
        if (oldTimestamp) {
            const currentTimestamp = Date.now() // Current time in milliseconds
            const differenceInMilliseconds = currentTimestamp - oldTimestamp
            const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60) // covert to hours
            return differenceInHours >= JWTTokenExpiryTime
        }
        return true
    }
    // generate JWT token
    async getJWTToken(isForce = false) {
        if (!this.isOlderThan24Hours() && !isForce) {
            return secureLocalStorage.getItem('recoJWTToken')
        }
        try {
            const payload = {
                iss:
                    this.config?.ooredoo?.jwt?.issuer ??
                    ConfigHelper.getInstance().getConfig().getAllJumpConfig.jwt.issuer,
                aud:
                    this.config?.ooredoo?.jwt?.audience ??
                    ConfigHelper.getInstance().getConfig().getAllJumpConfig.jwt.audience,
                iat: Math.floor(Date.now() / 1000),
                exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
                sub:
                    this.config.ooredoo?.jwt?.subject ??
                    ConfigHelper.getInstance().getConfig().getAllJumpConfig.jwt.subject,
                jti:
                    this.config?.ooredoo?.jwt?.jti ??
                    ConfigHelper.getInstance().getConfig().getAllJumpConfig.jwt.jti,
                roles:
                    this.config?.ooredoo?.jwt?.roles ??
                    ConfigHelper.getInstance().getConfig().getAllJumpConfig.jwt.roles,
            }

            const secretKey =
                this.config?.ooredoo?.jwt?.secretKey ??
                ConfigHelper.getInstance().getConfig().getAllJumpConfig.jwt.secretKey
            if (!secretKey) {
                throw new Error('Secret key must be defined.')
            }
            const headers =
                this.config?.ooredoo?.jwt?.headers ??
                ConfigHelper.getInstance().getConfig().getAllJumpConfig.jwt.headers
            const token = KJUR.jws.JWS.sign(
                headers.alg,
                JSON.stringify(headers),
                JSON.stringify(payload),
                secretKey
            )
            secureLocalStorage.setItem('recoJWTToken', token)
            DataHelper.getInstance().storeData([
                [DataHelper.STORE_KEY.RECOJWT_START_TIME, Date.now()],
            ])
            return token
        } catch (error) {
            this.logger.error('Error generating JWT:', error)
            return throwError(error)
        }
    }

    /**
     * verify SHA1
     */
    verifySHA1(originalString, hash) {
        const computedHash = this.hashStringSHA1(originalString)
        return computedHash === hash
    }
    /**
     * SHA1 function to convert Subscription ID
     */

    hashStringSHA1(message) {
        const hash = CryptoJS.SHA1(message)
        return hash.toString(CryptoJS.enc.Hex)
    }
    /**
     * Get all recommendation collections from JUump API.
     * @param {String} groupId
     * @param {String} laneId
     * @param {String} userProfileId
     * @param {String} customerResponse
     * @param {String} language
     * @param {String} render_preview
     * @returns {Observable<JSON[<items>]>}
     */

    getAllRecoFromApi(arg) {
        const {
            groupId,
            userProfileId,
            laneId,
            contentId,
            languageLocale,
            customerResponse = false,
            render_preview = false,
        } = arg
        const userProfileIdSHA1 = this.hashStringSHA1(userProfileId) // not used now

        //url for Similar Items
        const similarItemUrl = `${
            this.config?.urls?.recoUrl ??
            ConfigHelper.getInstance().getConfig().getAllJumpConfig.recoUrl
        }/laneGroups/${groupId}/lanes/${laneId}/render?userProfileId=${userProfileIdSHA1}&contentId=${contentId}&languageLocale=${languageLocale}&customerResponse=${customerResponse}&render_preview=${render_preview}`

        //url for Dynamic Swimlanes
        const dynamicSwimlanesUrl = `${
            this.config?.urls?.recoUrl ??
            ConfigHelper.getInstance().getConfig().getAllJumpConfig.recoUrl
        }/laneGroups/${groupId}/render?userProfileId=${userProfileIdSHA1}&languageLocale=${languageLocale}&customerResponse=${customerResponse}&render_preview=${render_preview}`

        const url =
            arg.groupId &&
            arg.userProfileId &&
            (!arg.contentId || arg.contentId === '') &&
            (!arg.laneId || arg.laneId === '') // Case where only groupId and userProfileId exist or both contentId and laneId are empty
                ? dynamicSwimlanesUrl
                : similarItemUrl

        return ajax({
            url,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.jwtToken}`,
            },
            method: 'GET',
            responseType: 'json',
            crossDomain: true,
        }).pipe(
            map((response) => {
                const data = response?.response || []
                if (data) {
                    if (url === similarItemUrl) {
                        const similarItems = {
                            title: data?.title,
                            items: data.items.map((d, index) => ({
                                contentId: d?.contentId,
                                slotNumber: config.useSlotNumber ? d.slotNumber : index + 1,
                                contentType: d?.customFields?.contentType,
                            })),
                        }
                        return similarItems
                    } else if (url === dynamicSwimlanesUrl) {
                        if (data?.carrousels && data.carrousels?.length) {
                            const result = data.carrousels.map((carousel, index) => ({
                                title: carousel?.title,
                                items: carousel.items.map((item, contentIndex) => ({
                                    contentId: item?.contentId,
                                    slotNumber: config?.useSlotNumber
                                        ? item?.slotNumber
                                        : contentIndex + 1,
                                    contentType: item?.customFields?.contentType,
                                })),
                            }))
                            const activeUniverse = arg?.groupId.replaceAll('-', '')
                            this.config.persistentStorage.multiSet([
                                [
                                    config?.storageKeys['reco_' + activeUniverse],
                                    JSON.stringify(result),
                                ],
                                [
                                    config?.storageKeys['lastRecoRetrieval_' + activeUniverse],
                                    Date.now().toString(),
                                ],
                            ])
                            return result
                        } else {
                            return []
                        }
                    }
                }
            }),
            catchError((error) => {
                if (error.status === 401) {
                    return this.getJWTToken(true).then(() => this.getAllReco(arg))
                }
                const transformedError =
                    error instanceof VoltError
                        ? error
                        : new VoltError(VoltError.codes.UNKNOWN_API_ERROR, {
                              inheritedError: error,
                          })
                return throwError(transformedError)
            })
        )
    }

    /**
     * Get all PPZ recommendation collections from Jump API.
     * @param {String} groupId
     * @param {String} laneId
     * @param {String} userProfileId
     * @param {String} customerResponse
     * @returns {Observable<JSON[<items>]>}
     */

    getAllPPZRecoFromApi(arg, retryCount = 0) {
        const {
            recommendationId,
            userProfileId,
            recommendationLaneId,
            customerResponse = false,
        } = arg
        const userProfileIdSHA1 = this.hashStringSHA1(userProfileId)
        const url = `${
            this.config?.urls?.recoUrl ??
            ConfigHelper.getInstance().getConfig().getAllJumpConfig.recoUrl
        }/laneGroups/${recommendationId}/lanes/${recommendationLaneId}/render?userProfileId=${userProfileIdSHA1}&customerResponse=${customerResponse}`
        return ajax({
            url,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.jwtToken}`,
            },
            method: 'GET',
            responseType: 'json',
            crossDomain: true,
        }).pipe(
            map((response) => {
                const data = response?.response || []
                const bannerData = {
                    ...arg,
                    title: data?.title,
                    items: data.items.map((d, index) => ({
                        contentId: d?.contentId,
                        slotNumber: config.useSlotNumber ? d.slotNumber : index + 1,
                        contentType: d?.customFields?.contentType,
                    })),
                }
                return bannerData
            }),
            catchError((error) => {
                if (error.status === 401 && retryCount <= 3) {
                    return this.getJWTToken(true).then(() =>
                        this.getAllPPZRecoFromApi(arg, retryCount + 1)
                    )
                }
                const transformedError =
                    error instanceof VoltError
                        ? error
                        : new VoltError(VoltError.codes.UNKNOWN_API_ERROR, {
                              inheritedError: error,
                          })
                return throwError(transformedError)
            })
        )
    }
    /**
     * Get all recommendation collections from server (if cache missing or expired) or from local storage.
     *
     * Get all recommendation collections.
     * @param {String} groupId
     * @param {String} laneId
     * @param {String} userProfileId
     * @param {String} customerResponse
     * @param {String} language
     * @param {String} render_preview
     * @returns {Observable<JSON[<items>]>}
     */
    getAllReco(arg) {
        if ((!arg.contentId || arg.contentId !== '') && (arg.laneId || arg.laneId !== '')) {
            return this.getAllRecoFromApi(arg)
        } else {
            const activeUniverse = arg?.groupId.replaceAll('-', '')
            return from(
                this.config.persistentStorage.getItem(
                    config?.storageKeys['lastRecoRetrieval_' + activeUniverse]
                )
            ).pipe(
                mergeMap((lastRecoRetrieval) => {
                    const differenceInSeconds = (Date.now() - lastRecoRetrieval) / 1000
                    // Cache still valid: retrieve recommendation from it
                    if (lastRecoRetrieval && differenceInSeconds < jumpCallPeriodically) {
                        return from(
                            this.config.persistentStorage.getItem(
                                config.storageKeys['reco_' + activeUniverse]
                            )
                        ).pipe(
                            map((recoJson) => JSON.parse(recoJson)),
                            // Error during cache retrieval ? ask to fetch reco
                            catchError(() => throwError(new Error('FETCH_RECO'))) // TODO - Upgrade this error? (10/2021)
                        )
                    }

                    // Throw error to ask to fetch reco
                    return throwError(new Error('FETCH_RECO')) // TODO - Upgrade this error? (10/2021)
                }),
                catchError((err) => {
                    // This specific error is awaited, meaning that reco should be fetch
                    if (err.message === 'FETCH_RECO') {
                        return this.getAllRecoFromApi(arg).pipe(
                            mergeMap((results) => {
                                return from(
                                    this.config?.persistentStorage?.multiSet([
                                        [
                                            config?.storageKeys['reco_' + activeUniverse],
                                            JSON.stringify(results),
                                        ],
                                        [
                                            config?.storageKeys[
                                                'lastRecoRetrieval_' + activeUniverse
                                            ],
                                            Date.now(),
                                        ],
                                    ])
                                ).pipe(mapTo(results))
                            })
                        )
                    }

                    // Caught error was unexpected: rethrow it
                    return throwError(err)
                })
            )
        }
    }

    /**
     * Get all PPZ recommendation collections.
     * @param {String} recommendationId
     * @param {String} recommendationLaneId
     * @param {String} userProfileId
     * @param {String} customerResponse
     * @param {String} language
     * @param {String} render_preview
     * @returns {Observable<JSON[<items>]>}
     */
    getJumpRecoForPPZ(arg) {
        if (
            arg.recommendationId &&
            arg.recommendationId !== '' &&
            arg.recommendationLaneId &&
            arg.recommendationLaneId !== ''
        ) {
            return this.getAllPPZRecoFromApi(arg)
        } else {
            return of([])
        }
    }
}
